import React, { useEffect, useMemo, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import axios from 'axios';
import { Button, Card, Col, Empty, Input, InputNumber, Row, Select, Space, Switch, Table, Tag, Typography, message } from 'antd';
import { AUTHORITIES } from 'app/config/constants';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { getClasses, getClasse } from 'app/entities/classe/classe.reducer';
import { getPeriodes } from 'app/entities/periode-evaluation/periode-evaluation.reducer';
import { getEcoleActiveAnneeScolaire } from 'app/entities/annee-scolaire/annee-scolaire.reducer';
import { getMatieresByClasseId } from 'app/entities/matiere-ecole/matiere-ecole.reducer';
import { useHasAnyAuthority } from 'app/hooks/useHasAnyAuthority';
import { StatutPeriodeEvaluation } from 'app/shared/model/periode-evaluation.model';
import {
  clearNoteEditorState,
  getEvaluationDetail,
  getEvaluationsByClasseAndPeriode,
  getEvaluationNotes,
  NoteRow,
  saveNotesBatch,
} from './note.reducer';
import { getNotesReturnPath } from './notes-navigation';

const { Title } = Typography;

export const Notes = (props: RouteComponentProps<{ ecoleid: string; anneeScolaireId: string }>) => {
  const dispatch = useAppDispatch();
  const isAdmin = useHasAnyAuthority([AUTHORITIES.ADMIN]);
  const classes = useAppSelector(state => state.classe.entities);
  const classeEntity = useAppSelector(state => state.classe.entity);
  const periodes = useAppSelector(state => state.periodeEvaluation.entities);
  const matieres = useAppSelector(state => state.matiereEcole.entities);
  const evaluations = useAppSelector(state => state.note.evaluations);
  const evaluationDetail = useAppSelector(state => state.note.evaluationDetail);
  const serverRows = useAppSelector(state => state.note.noteRows);
  const notesStatus = useAppSelector(state => state.note.notesStatus);
  const periodeStatut = useAppSelector(state => state.note.periodeStatut);
  const loading = useAppSelector(state => state.note.notesLoading);
  const updating = useAppSelector(state => state.note.updating);

  const [classeId, setClasseId] = useState<string | null>(null);
  const [periodeId, setPeriodeId] = useState<string | null>(null);
  const [evaluationId, setEvaluationId] = useState<string | null>(null);
  const [rows, setRows] = useState<NoteRow[]>([]);
  const [prefillDone, setPrefillDone] = useState(false);
  const [sousMatiereNom, setSousMatiereNom] = useState('');
  const { anneeScolaireId } = props.match.params;
  const queryParams = useMemo(() => new URLSearchParams(props.location.search || ''), [props.location.search]);
  const evaluationParam = useMemo(() => {
    return queryParams.get('evaluationId');
  }, [queryParams]);
  const returnTo = useMemo(() => {
    const origin = queryParams.get('returnTo');
    return origin === 'classe' ? 'classe' : 'evaluation';
  }, [queryParams]);
  const classeIdFromQuery = useMemo(() => {
    return queryParams.get('classeId');
  }, [queryParams]);

  const navigateBack = () => {
    const targetClasseId = classeIdFromQuery || classeId || evaluationDetail?.classeId;
    props.history.push(
      getNotesReturnPath({
        ecoleId: props.match.params.ecoleid,
        anneeScolaireId,
        classeId: targetClasseId,
        returnTo,
      })
    );
  };

  useEffect(() => {
    if (classeIdFromQuery && !classeId) {
      setClasseId(classeIdFromQuery);
    }
  }, [classeIdFromQuery, classeId]);

  useEffect(() => {
    dispatch(getClasses(props.match.params.ecoleid));
    dispatch(getEcoleActiveAnneeScolaire(props.match.params.ecoleid));
  }, []);

  useEffect(() => {
    setRows(serverRows);
  }, [serverRows]);

  useEffect(() => {
    const evalId = evaluationParam;
    if (!evalId || prefillDone) return;

    const prefill = async () => {
      try {
        const evaluation = await dispatch(getEvaluationDetail(evalId)).unwrap();
        if (evaluation?.classeId) {
          setClasseId(evaluation.classeId);
        }
        if (evaluation?.periodeId) {
          setPeriodeId(evaluation.periodeId);
        }
        setEvaluationId(evaluation?.id || evalId);
      } finally {
        setPrefillDone(true);
      }
    };

    prefill();
  }, [evaluationParam, prefillDone]);

  useEffect(() => {
    if (!evaluationParam) {
      setPeriodeId(null);
      setEvaluationId(null);
      dispatch(clearNoteEditorState());
    }

    if (classeId) {
      dispatch(getClasse(classeId));
      dispatch(getMatieresByClasseId(classeId));
    }
  }, [classeId, evaluationParam]);

  useEffect(() => {
    if (classeEntity?.id === classeId && classeEntity?.cycleId && anneeScolaireId) {
      dispatch(getPeriodes({ ecoleId: props.match.params.ecoleid, anneeScolaireId, cycleId: classeEntity.cycleId }));
    }
  }, [classeEntity?.id, classeEntity?.cycleId, classeId, anneeScolaireId]);

  useEffect(() => {
    if (!evaluationParam) {
      setEvaluationId(null);
      dispatch(clearNoteEditorState());
    }
  }, [periodeId, evaluationParam]);

  useEffect(() => {
    if (!classeId || !periodeId) {
      dispatch(clearNoteEditorState());
      return;
    }

    dispatch(getEvaluationsByClasseAndPeriode({ classeId, periodeId }));
  }, [classeId, periodeId]);

  useEffect(() => {
    if (evaluationId) {
      dispatch(getEvaluationNotes(evaluationId));
    } else {
      dispatch(clearNoteEditorState());
    }
  }, [evaluationId]);

  const handleSave = async () => {
    if (!evaluationId) return;
    if (isLocked) {
      message.warning(isPeriodLocked ? "Saisie verrouillée: la période n'est pas ouverte" : 'Saisie verrouillée: notes terminées');
      return;
    }

    try {
      await dispatch(saveNotesBatch({ evaluationId, rows })).unwrap();
      message.success('Notes enregistrées');
      navigateBack();
    } catch {
      message.error("Impossible d'enregistrer les notes");
    }
  };

  const matiereNom = useMemo(() => {
    if (!evaluationDetail?.matiereId) return '';
    const matiere = matieres.find(m => m.id === evaluationDetail.matiereId);
    return matiere?.nom || '';
  }, [evaluationDetail?.matiereId, matieres]);

  useEffect(() => {
    const loadSousMatiere = async () => {
      if (!evaluationDetail?.sousMatiereId) {
        setSousMatiereNom('');
        return;
      }
      const response = await axios.get(`/api/sous-matieres/${evaluationDetail.sousMatiereId}`);
      setSousMatiereNom(response.data?.nom || '');
    };
    loadSousMatiere();
  }, [evaluationDetail?.sousMatiereId]);

  const periodeNom = useMemo(() => {
    if (!evaluationDetail?.periodeId) return '';
    const periode = periodes.find(p => p.id === evaluationDetail.periodeId);
    return periode?.nom || '';
  }, [evaluationDetail?.periodeId, periodes]);

  const isPeriodLocked = periodeStatut !== null && periodeStatut !== StatutPeriodeEvaluation.OUVERTE;
  const isLocked = isPeriodLocked || (notesStatus === 'Terminée' && !isAdmin);

  const columns = [
    { title: 'Matricule', dataIndex: 'matricule', key: 'matricule' },
    { title: 'Nom', dataIndex: 'nom', key: 'nom' },
    { title: 'Prénom', dataIndex: 'prenom', key: 'prenom' },
    {
      title: 'Absent',
      key: 'absent',
      render: (_, record, index) => (
        <Switch
          checked={record.absent}
          disabled={isLocked}
          onChange={checked => {
            if (isLocked) return;
            const copy = [...rows];
            copy[index] = { ...copy[index], absent: checked, valeur: checked ? null : copy[index].valeur };
            setRows(copy);
          }}
        />
      ),
    },
    {
      title: 'Note',
      key: 'valeur',
      render: (_, record, index) => (
        <Space>
          <InputNumber
            value={record.valeur ?? undefined}
            disabled={isLocked || record.absent}
            min={0}
            max={evaluationDetail?.bareme ?? undefined}
            onChange={value => {
              if (isLocked) return;
              const copy = [...rows];
              let nextValue = (value as number | null) ?? null;

              if (evaluationDetail?.bareme !== undefined && nextValue !== null && nextValue > evaluationDetail.bareme) {
                nextValue = evaluationDetail.bareme;
                message.warning(`La note ne peut pas dépasser ${evaluationDetail.bareme}`);
              }

              copy[index] = { ...copy[index], valeur: nextValue };
              setRows(copy);
            }}
          />
          <span>/ {evaluationDetail?.bareme}</span>
        </Space>
      ),
    },
    {
      title: 'Appréciation',
      key: 'appreciation',
      render: (_, record, index) => (
        <Input
          style={{ width: '50%' }}
          value={record.appreciation}
          disabled={isLocked}
          onChange={e => {
            if (isLocked) return;
            const copy = [...rows];
            copy[index] = { ...copy[index], appreciation: e.target.value };
            setRows(copy);
          }}
        />
      ),
    },
  ];

  return (
    <Card style={{ margin: '24px' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            Saisie des notes
          </Title>
        </Col>
        <Col>
          <Button onClick={navigateBack} type="default">
            Retour
          </Button>
        </Col>
      </Row>

      {evaluationId && (
        <Card
          style={{ marginBottom: 16, borderRadius: 10, background: '#f8fafc', border: '1px solid #e2e8f0' }}
          bodyStyle={{ padding: '16px 20px' }}
        >
          <Row gutter={[16, 12]}>
            <Col xs={24} sm={12} md={6}>
              <Typography.Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Évaluation
              </Typography.Text>
              <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a', marginTop: 2 }}>{evaluationDetail?.titre || '-'}</div>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Typography.Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Classe
              </Typography.Text>
              <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a', marginTop: 2 }}>{classeEntity?.nom || '-'}</div>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Typography.Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Matière
              </Typography.Text>
              <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a', marginTop: 2 }}>
                {sousMatiereNom ? `${matiereNom || '-'} / ${sousMatiereNom}` : matiereNom || '-'}
              </div>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Typography.Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Type
              </Typography.Text>
              <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a', marginTop: 2 }}>{evaluationDetail?.type || '-'}</div>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Typography.Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Période
              </Typography.Text>
              <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a', marginTop: 2 }}>{periodeNom || '-'}</div>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Typography.Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Statut période
              </Typography.Text>
              <div style={{ marginTop: 4 }}>
                {periodeStatut ? (
                  <Tag
                    color={
                      periodeStatut === StatutPeriodeEvaluation.OUVERTE
                        ? 'green'
                        : periodeStatut === StatutPeriodeEvaluation.FERMEE
                        ? 'red'
                        : 'blue'
                    }
                  >
                    {periodeStatut === StatutPeriodeEvaluation.OUVERTE
                      ? 'Ouverte'
                      : periodeStatut === StatutPeriodeEvaluation.FERMEE
                      ? 'Fermée'
                      : 'Planifiée'}
                  </Tag>
                ) : (
                  <span style={{ color: '#94a3b8' }}>-</span>
                )}
              </div>
            </Col>
          </Row>
        </Card>
      )}

      {!evaluationParam && (
        <Row gutter={[16, 16]}>
          <Col xs={24} md={6}>
            <Select
              placeholder="Classe"
              style={{ width: '100%' }}
              value={classeId || undefined}
              onChange={v => setClasseId(v || null)}
              allowClear
            >
              {classes.map(classe => (
                <Select.Option key={classe.id} value={classe.id}>
                  {classe.nom}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} md={6}>
            <Select
              placeholder="Période"
              style={{ width: '100%' }}
              value={periodeId || undefined}
              onChange={v => setPeriodeId(v || null)}
              allowClear
              disabled={!classeId}
            >
              {periodes.map(periode => (
                <Select.Option key={periode.id} value={periode.id}>
                  {periode.nom}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} md={12}>
            <Select
              placeholder="Évaluation"
              style={{ width: '100%' }}
              value={evaluationId || undefined}
              onChange={v => setEvaluationId(v || null)}
              allowClear
              disabled={!classeId || !periodeId}
            >
              {evaluations.map(evaluation => (
                <Select.Option key={evaluation.id} value={evaluation.id}>
                  {evaluation.titre}
                </Select.Option>
              ))}
            </Select>
          </Col>
        </Row>
      )}

      <div style={{ marginTop: 16 }}>
        {evaluationId ? (
          <Table rowKey="inscriptionId" loading={loading || updating} columns={columns} dataSource={rows} pagination={false} />
        ) : (
          <Empty description="Sélectionnez une classe, une période et une évaluation" />
        )}
      </div>

      <Space style={{ marginTop: 16 }}>
        <Button type="primary" onClick={handleSave} disabled={!evaluationId || loading || updating || isLocked}>
          Enregistrer
        </Button>
      </Space>
    </Card>
  );
};

export default Notes;
